import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface User {
  username: string;
  role: 'admin' | 'client';
}

export interface ClientAccount {
  id: string;
  username: string;
  password: string;
  name: string;
  email: string;
  createdAt: string;
  isActive: boolean;
}

interface AuthContextType {
  user: User | null;
  login: (username: string, password: string) => boolean;
  logout: () => void;
  isAuthenticated: boolean;
  addClient: (account: Omit<ClientAccount, 'id' | 'createdAt'>) => { success: boolean; error?: string };
  updateClient: (id: string, updates: Partial<Omit<ClientAccount, 'id' | 'createdAt'>>) => { success: boolean; error?: string };
  deleteClient: (id: string) => boolean;
  getClients: () => ClientAccount[];
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Hardcoded admin credentials (always available)
const ADMIN_CREDENTIAL = {
  username: 'admin',
  password: 'admin123',
  role: 'admin' as const
};

const STORAGE_KEY = 'admin_client_accounts';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [clients, setClients] = useState<ClientAccount[]>([]);

  // Load clients from localStorage
  useEffect(() => {
    const storedUser = localStorage.getItem('auth_user');
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }

    const storedClients = localStorage.getItem(STORAGE_KEY);
    if (storedClients) {
      try {
        setClients(JSON.parse(storedClients));
      } catch (e) {
        console.error('Failed to parse client accounts', e);
        setClients([]);
      }
    }
  }, []);

  // Save clients to localStorage whenever they change
  const saveClients = (updatedClients: ClientAccount[]) => {
    setClients(updatedClients);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedClients));
  };

  const login = (username: string, password: string): boolean => {
    // Check admin credentials
    if (username === ADMIN_CREDENTIAL.username && password === ADMIN_CREDENTIAL.password) {
      const userData = { username: ADMIN_CREDENTIAL.username, role: ADMIN_CREDENTIAL.role };
      setUser(userData);
      localStorage.setItem('auth_user', JSON.stringify(userData));
      return true;
    }

    // Check dynamic client accounts
    const client = clients.find(
      c => c.username === username && c.password === password && c.isActive
    );

    if (client) {
      const userData = { username: client.username, role: 'client' as const };
      setUser(userData);
      localStorage.setItem('auth_user', JSON.stringify(userData));
      return true;
    }

    return false;
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('auth_user');
  };

  const addClient = (account: Omit<ClientAccount, 'id' | 'createdAt'>): { success: boolean; error?: string } => {
    // Check if username already exists
    if (clients.some(c => c.username === account.username)) {
      return { success: false, error: 'Username already exists' };
    }

    // Check if username is 'admin'
    if (account.username === 'admin') {
      return { success: false, error: 'Username "admin" is reserved' };
    }

    const newClient: ClientAccount = {
      ...account,
      id: `client_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      createdAt: new Date().toISOString()
    };

    saveClients([...clients, newClient]);
    return { success: true };
  };

  const updateClient = (id: string, updates: Partial<Omit<ClientAccount, 'id' | 'createdAt'>>): { success: boolean; error?: string } => {
    const clientIndex = clients.findIndex(c => c.id === id);
    if (clientIndex === -1) {
      return { success: false, error: 'Client not found' };
    }

    // Check username uniqueness if username is being updated
    if (updates.username && updates.username !== clients[clientIndex].username) {
      if (clients.some(c => c.username === updates.username)) {
        return { success: false, error: 'Username already exists' };
      }
      if (updates.username === 'admin') {
        return { success: false, error: 'Username "admin" is reserved' };
      }
    }

    const updatedClients = [...clients];
    updatedClients[clientIndex] = {
      ...updatedClients[clientIndex],
      ...updates
    };

    saveClients(updatedClients);
    return { success: true };
  };

  const deleteClient = (id: string): boolean => {
    const filteredClients = clients.filter(c => c.id !== id);
    if (filteredClients.length === clients.length) {
      return false; // Client not found
    }
    saveClients(filteredClients);
    return true;
  };

  const getClients = (): ClientAccount[] => {
    return [...clients];
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      login, 
      logout, 
      isAuthenticated: !!user,
      addClient,
      updateClient,
      deleteClient,
      getClients
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}